import React from 'react';

export interface IModalProps {
  /**
   * Can be used to give an element an accessible description.
   * If presented, it is anounced by screen reader when dialog opens
   * Space separated list of one ore more ID references.
   */
  ariaDescribedby?: string;

  /**
   * Can be used to give an element an accessible name.
   * Space separated list of one ore more ID references.
   */
  ariaLabelledby?: string;

  /**
   * This props handles 3 things:
   * 1) automatically moves focus on the dialog when it is opened.
   * 2) keeps focus inside the modal while it is open.
   * 3) moves focus back to the previously focused element on close.
   *
   * Normally should always be true for accessibility.
   * @default true
   */
  autofocus?: boolean;

  /**
   * The backdrop configuration. Includes a modal-backdrop element. Alternatively,
   * specify static for a backdrop which doesn't close the modal on click.
   * @default true
   * @see https://getbootstrap.com/docs/3.3/javascript/#modals-options
   */
  backdrop?: boolean | 'static';

  /**
   * Closes the modal when escape key is pressed
   * @default true
   * @see https://getbootstrap.com/docs/3.3/javascript/#modals-options
   */
  keyboard?: boolean;

  /**
   * Indicates if this Modal should be visible.
   */
  show: boolean;

  /**
   * Specify the size of this modal.
   *
   * @default regular
   */
  size?: 'small' | 'regular' | 'large' | 'fullscreen' | 'slender';

  /**
   * Helper attribute for QA. Adds "qa-id" attribute to component's container
   */
  'qa-id'?: string;

  /**
   * This callback must control the value of "show" property.
   * It is envoked when a Modal is expected to be hidden as a result of one of:
   * 1) clicking "X" button in the header;
   * 2) when **ModalCloseBtn** is clicked;
   * 3) when "ESC" key is pressed (and "keyboard" == true);
   * 4) when backdrop is clicked (and "backdrop" == true);
   * It is not called if the dom node is removed by any other reason.
   * Or when developer provides his own "close" button.
   * @default null
   */
  onHide: () => void;

  /**
   * Turn off/on the dragging.
   * @default true
   */
  draggable?: boolean;
}

export interface IModalBackdropProps {
  zIndex?: number;
  /**
   * Helper attribute for QA. Adds "qa-id" attribute to component's container
   */
  'qa-id'?: string;
  pose: string;
  onHide: () => void;
}

export interface IModalBody extends IInjectedModalProps {
  /** Ability to set class name */
  className?: string;
}

export interface IModalCloseBtnProps extends IInjectedModalProps {
  /**
   * The label for the button.
   * @default 'Cancel'
   */
  label?: string | React.ReactElement<HTMLElement>;
}

/**
 * Describes props which take values from ModalContext.
 * These props are injected into Modal's subcomponents by "withModalContext" HOC.
 * Should not be used by user.
 */
export interface IInjectedModalProps {
  /**
   * @ignore
   */
  draggable: boolean;

  /**
   * @ignore
   */
  isFullScreen: boolean;

  /**
   * @ignore
   */
  handleClose(): void;
}

export const ModalContext = React.createContext({
  draggable: true,
  handleClose: () => {
    /* Modal component will set a handler */
  },
  isFullScreen: false
});

export interface IModalHeaderProps extends IInjectedModalProps {
  /**
   * The Title for this Modal header.
   */
  title?: string | React.ReactElement<{}>;

  /**
   * Show and enable the close button for this header
   */
  closeButton?: boolean;
}
